import React, { type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useApprovals, useApprovalsDispatch } from '../ApprovalsContext.tsx';
import { PressableLink } from '../PressableLink.tsx';

const i18n = defineMessages({
	approvalInProgressBannerPrefix: {
		id: 'approvals.editor.approval-in-progress.banner.prefix',
		description:
			'Prefix of banner message when user is on edit page but approval is pending - editing is disabled.',
		defaultMessage: 'Reminder: editing is locked while an approval is pending.',
	},
	approvalInProgressCancelCta: {
		id: 'approvals.editor.approval-in-progress.banner.cancel-cta',
		description: 'CTA link to cancel the approval when editing is locked due to a pending approval.',
		defaultMessage: 'Cancel approval',
	},
	approvedBannerPrefix: {
		id: 'approvals.editor.approved.banner.prefix',
		description:
			'Prefix of banner message when user is on edit page but page is approved - editing is disabled.',
		defaultMessage: 'Reminder: You cannot edit an approved page without clearing the approval.',
	},
	approvedClearCta: {
		id: 'approvals.editor.approved.banner.clear-cta',
		description: 'CTA link to clear the approval when editing is locked due to an approved review.',
		defaultMessage: 'Clear approval',
	},
});

const styles = cssMap({
	banner: {
		height: '40px',
		paddingBlock: token('space.050'),
		paddingInline: token('space.250'),
		backgroundColor: token('elevation.surface.sunken'),
	},
});

/**
 * When approval is in progress (isDraftLockedForEditing), shows a banner explaining that editing
 * is not allowed. Renders nothing when there is no approval in progress or when changes are
 * requested (DENIED status) — in that case editing is permitted.
 *
 * - PENDING: "Reminder: editing is locked while an approval is pending. Cancel approval"
 * - APPROVED: "Reminder: You cannot edit an approved page without clearing the approval. Clear approval."
 * - DENIED / no lock: renders nothing
 */
export const ApprovalInProgressEditorBlocker: FC = () => {
	const { formatMessage } = useIntl();
	const { isDraftLockedForEditing, approvalRequest } = useApprovals();
	const { cancelWorkflowApplication } = useApprovalsDispatch();

	if (!isDraftLockedForEditing) {
		return null;
	}

	const handleCancelWorkflowApplication = () => {
		void cancelWorkflowApplication();
	};

	const reviewStatus = approvalRequest?.review?.reviewStatus;

	if (reviewStatus === 'APPROVED') {
		return (
			<Flex xcss={styles.banner} alignItems="center" justifyContent="center">
				<Text>
					{formatMessage(i18n.approvedBannerPrefix)}{' '}
					<PressableLink onClick={handleCancelWorkflowApplication}>
						{formatMessage(i18n.approvedClearCta)}
					</PressableLink>
				</Text>
			</Flex>
		);
	}

	// reviewStatus === 'PENDING' (the only remaining locked state)
	return (
		<Flex xcss={styles.banner} alignItems="center" justifyContent="center">
			<Text>
				{formatMessage(i18n.approvalInProgressBannerPrefix)}{' '}
				<PressableLink onClick={handleCancelWorkflowApplication}>
					{formatMessage(i18n.approvalInProgressCancelCta)}
				</PressableLink>
			</Text>
		</Flex>
	);
};
